import { useEffect, useRef, useState } from 'react';
import { Box, Button, Center, Stack, Text, TextInput, Title } from '@mantine/core';

interface SetupScreenProps {
  /**
   * Optional error message displayed instead of the default pairing prompt.
   * When set, the screen acts as the "wrong code / no connection" panel and
   * still presents the pairing form below so the operator can try again
   * without leaving the page.
   */
  message?: string;
}

/**
 * Shown when the kiosk URL has no pairing code (`/`) or when the supplied
 * code is unknown to the API. Lets the operator type the code their
 * administrator gave them and submit — that navigates to `/k/{code}` which
 * triggers the OIDC flow and renders the live kiosk.
 *
 * Designed for an operator standing in front of an unpaired kiosk: large
 * touch-friendly input, visible code monospace, single primary action.
 */
export function SetupScreen({ message }: SetupScreenProps) {
  const isError = Boolean(message);
  const [code, setCode] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus the input on mount so a barcode reader (or a keyboard) can
  // start typing immediately. Re-focus when an error message appears so
  // retrying is one-handed.
  useEffect(() => {
    inputRef.current?.focus();
  }, [isError]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim().toLowerCase();
    if (!trimmed) return;
    // Full navigation so the React tree re-mounts under the new pairing
    // code; useKioskConfigByCode in App.tsx picks it up from the URL.
    window.location.href = `/k/${encodeURIComponent(trimmed)}`;
  };

  return (
    <Center h="100dvh" style={{ backgroundColor: 'var(--bg-base)' }}>
      <Stack align="center" gap={24} style={{ maxWidth: 480, padding: 'var(--space-6)', width: '100%' }}>
        <Box
          style={{
            width: 88,
            height: 88,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(59, 130, 246, 0.05) 100%)',
            border: '1px solid rgba(59, 130, 246, 0.20)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--cmd-accent)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
            <line x1="8" y1="21" x2="16" y2="21" />
            <line x1="12" y1="17" x2="12" y2="21" />
          </svg>
        </Box>

        <Stack align="center" gap={8}>
          <Title
            order={2}
            ta="center"
            fw={700}
            style={{
              fontFamily: 'var(--font-heading)',
              color: 'var(--text-primary)',
              fontSize: '1.5rem',
              letterSpacing: '-0.01em',
            }}
          >
            {isError ? 'Kiosk code not recognised' : 'Pair this kiosk'}
          </Title>
          <Text
            ta="center"
            fw={500}
            style={{
              fontFamily: 'var(--font-body)',
              color: 'var(--text-secondary)',
              fontSize: '0.9375rem',
              lineHeight: 1.6,
            }}
          >
            {isError
              ? message
              : 'Your administrator can give you a pairing code for this kiosk. Enter it below to get started.'}
          </Text>
        </Stack>

        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <Stack gap={12}>
            <TextInput
              ref={inputRef}
              label="Kiosk code"
              placeholder="e.g. md-k1"
              value={code}
              onChange={(e) => setCode(e.currentTarget.value)}
              size="lg"
              autoComplete="off"
              spellCheck={false}
              autoCapitalize="off"
              styles={{
                label: {
                  color: 'var(--text-secondary)',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  marginBottom: 6,
                },
                input: {
                  backgroundColor: 'var(--bg-card)',
                  border: '1px solid var(--border-colour)',
                  color: 'var(--text-primary)',
                  fontFamily: 'var(--font-mono, ui-monospace, monospace)',
                  fontSize: '1.125rem',
                  letterSpacing: '0.02em',
                  textAlign: 'center',
                  minHeight: 56,
                },
              }}
            />
            <Button
              type="submit"
              size="lg"
              disabled={code.trim().length === 0}
              fullWidth
              style={{ minHeight: 56, fontSize: '1rem', fontWeight: 600 }}
            >
              {isError ? 'Try a different code' : 'Pair kiosk'}
            </Button>
          </Stack>
        </form>

        <Text
          mt={16}
          fw={600}
          style={{
            fontFamily: 'var(--font-heading)',
            color: 'var(--text-muted)',
            fontSize: '0.6875rem',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          Commander Equipment Kiosk
        </Text>
      </Stack>
    </Center>
  );
}
